import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { useEffect } from "react";

// Suppress cross-origin iframe errors from third-party analytics (PostHog, PayPal)
if (typeof window !== 'undefined') {
  // Override window.onerror to catch and suppress cross-origin errors
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const msg = String(message || '');
    if (msg.includes('cross-origin') || 
        msg.includes('removeEventListener') ||
        msg.includes('SecurityError') ||
        msg.includes('Script error') ||
        (source && source.includes('posthog'))) {
      return true; // Suppress the error
    }
    if (originalOnError) {
      return originalOnError.apply(this, arguments);
    }
    return false;
  };

  window.addEventListener('error', (event) => {
    const msg = event.message || '';
    if (msg.includes('cross-origin') || 
        msg.includes('removeEventListener') ||
        msg.includes('SecurityError') ||
        msg.includes('Script error')) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }, true);
  
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || String(event.reason || '');
    if (reason.includes('cross-origin') || reason.includes('SecurityError')) {
      event.preventDefault();
    }
  });
}

// Pages
import Landing from "./pages/Landing";
import About from "./pages/About";
import Login from "./pages/Login";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Journey from "./pages/Journey";
import ClarityPhase from "./pages/phases/ClarityPhase";
import AssetsPhase from "./pages/phases/AssetsPhase";
import AuthorityPhase from "./pages/phases/AuthorityPhase";
import LaunchPhase from "./pages/phases/LaunchPhase";
import SavedOutputs from "./pages/SavedOutputs";

// Legacy imports for backward compatibility
import Dashboard from "./pages/Dashboard";
import ClarityRoom from "./pages/rooms/ClarityRoom";
import AssetBuilderRoom from "./pages/rooms/AssetBuilderRoom";
import AuthorityAssetsRoom from "./pages/rooms/AuthorityAssetsRoom";
import CalmLaunchRoom from "./pages/rooms/CalmLaunchRoom";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-[#2C2C2C]/60 font-light">One moment...</p>
        </div>
      </div>
    );
  }

  // If user data was passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      
      {/* New Blueprint Flow */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journey"
        element={
          <ProtectedRoute>
            <Journey />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phase/clarity"
        element={
          <ProtectedRoute>
            <ClarityPhase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phase/assets"
        element={
          <ProtectedRoute>
            <AssetsPhase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phase/authority"
        element={
          <ProtectedRoute>
            <AuthorityPhase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/phase/launch"
        element={
          <ProtectedRoute>
            <LaunchPhase />
          </ProtectedRoute>
        }
      />
      
      {/* Legacy routes redirect to new flow */}
      <Route
        path="/dashboard"
        element={<Navigate to="/journey" replace />}
      />
      <Route
        path="/room/clarity"
        element={<Navigate to="/phase/clarity" replace />}
      />
      <Route
        path="/room/asset-builder"
        element={<Navigate to="/phase/assets" replace />}
      />
      <Route
        path="/room/authority"
        element={<Navigate to="/phase/authority" replace />}
      />
      <Route
        path="/room/launch"
        element={<Navigate to="/phase/launch" replace />}
      />
      
      <Route
        path="/saved"
        element={
          <ProtectedRoute>
            <SavedOutputs />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="grain-overlay" aria-hidden="true"></div>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#FDFBF7',
            border: '1px solid #E5E0D6',
            color: '#2C2C2C',
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;
